import { type ComponentType } from 'react';

/** Marks a reload already attempted, so a dead server cannot loop the page. */
const RECOVERY_FLAG = 'meeply.chunkReload';

/**
 * Recognises a *module load* failure, never an error thrown by the module.
 *
 * Warning: Reloading on the latter would reproduce it and trap the reader.
 *
 * @param error Whatever the dynamic import rejected with.
 * @returns `true` when the module could not be fetched at all.
 * @see docs/features/auth.md
 */
function isModuleLoadFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed')
  );
}

const readFlag = (): boolean => {
  try {
    return sessionStorage.getItem(RECOVERY_FLAG) === '1';
  } catch {
    // `sessionStorage` unavailable: better not to reload at all than risk a
    // loop we would have no way of stopping.
    return true;
  }
};

const writeFlag = (value: boolean): void => {
  try {
    if (value) sessionStorage.setItem(RECOVERY_FLAG, '1');
    else sessionStorage.removeItem(RECOVERY_FLAG);
  } catch {
    /* harmless */
  }
};

/**
 * Wraps a screen's dynamic import, reloading once if its chunk is gone.
 *
 * Warning: A missing chunk happens on **every deploy** to an already-open tab.
 *
 * @param load Imports the screen component.
 * @returns The route loader the router expects.
 * @see docs/features/auth.md
 */
export function lazyRoute(load: () => Promise<ComponentType>) {
  return async (): Promise<{ Component: ComponentType }> => {
    try {
      const Component = await load();
      // A successful load gives the next failure its own attempt.
      writeFlag(false);
      return { Component };
    } catch (error) {
      if (!isModuleLoadFailure(error) || readFlag()) throw error;

      writeFlag(true);
      globalThis.location.reload();

      // Never resolves: the page is reloading, and the router would otherwise
      // show an error in the meantime.
      return new Promise<never>(() => undefined);
    }
  };
}
